import { useState } from "react";
import { Menu } from "lucide-react";

import { NAV_ITEMS } from "./sidebar.constants";
import { SidebarAgentsList } from "./components/SidebarAgentsList";
import { SidebarChatList } from "./components/SidebarChatList";
import { SidebarFooter } from "./components/SidebarFooter";
import { Button } from "@/components/ui/button";
import Logo from "@/assets/images/Logo.png";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { goTo } from "@/store/navigationSlice";
import { fetchAgentsOnce } from "@/store/agentsSlice";
import { fetchAllConversations } from "@/store/conversationsSlice";
import { toggleSidebar } from "@/store/uiSlice";
import { useEnvVisibility } from "@/hooks/visibility.hook";


export function Sidebar() {
  const dispatch = useAppDispatch();

  const activeTab = useAppSelector((s) => s.navigation.activePage);
  const isCollapsed = useAppSelector((s) => s.ui.isSidebarCollapsed);
  const agentsLoaded = useAppSelector((s) => s.agents.agentsLoaded);
  const conversationsLoaded = useAppSelector((s) => s.conversations.conversationsLoaded);

  const [isChatsExpanded, setIsChatsExpanded] = useState(false);
  const [isAgentsExpanded, setIsAgentsExpanded] = useState(false);
   const {isQA1, isQA2 } = useEnvVisibility();

  return (
    <aside
      className={`${isCollapsed ? "w-16" : "w-64"} h-screen border-r bg-card p-4 flex flex-col transition-all duration-300 ease-in-out`}
    >
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => dispatch(toggleSidebar())}
          className="cursor-pointer"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {!isCollapsed && (
          <div className="mb-1">
            <img alt="My Paramount" src={Logo} className="h-8 w-auto" />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-2">
        {NAV_ITEMS
          .filter((item: any) => {
            // In QA environments, hide items with qa_visibility: false
            if ((isQA1 || isQA2) && item.qa_visibility === false) {
              return false;
            }
            return true;
          })
          .map((item: any) => {
          const Icon = item.icon;
          const isChatsItem = item.id === "chats";
          const isAgentsItem = item.id === "agents";

          return (
            <div key={item.id} className="relative">
              <Button
                variant={activeTab === item.id ? "secondary" : "ghost"}
                className={`w-full ${isCollapsed ? "justify-center" : "justify-start"} cursor-pointer relative`}
                onClick={() => {
                  if (isChatsItem) {
                      if (isCollapsed) {
                      dispatch(toggleSidebar());
                    }

                    setIsChatsExpanded(!isChatsExpanded);
                    
                    //  Fetch agents only once
                    if (!conversationsLoaded) {
                      dispatch(fetchAllConversations());
                    }
                  } else if (isAgentsItem) {
                    // If sidebar is collapsed → expand it
                    if (isCollapsed) {
                      dispatch(toggleSidebar());
                    }

                    //  Expand agents section
                    setIsAgentsExpanded(!isAgentsExpanded);

                    //  Fetch agents only once
                    if (!agentsLoaded) {
                      dispatch(fetchAgentsOnce());
                    }

                  } else {
                    dispatch(goTo(item.id));
                  }
                }}
                title={isCollapsed ? item.label : undefined}
              >
                <Icon className={`h-4 w-4 ${isCollapsed ? "" : "mr-3"}`} />

                {!isCollapsed && (
                  <>
                    {item.label}
                  </>
                )}
              </Button>

              {isAgentsItem && isAgentsExpanded && !isCollapsed && (
                <SidebarAgentsList />
              )}

              {isChatsItem && isChatsExpanded && !isCollapsed && (
                <SidebarChatList expanded={isChatsExpanded} />
              )}
            </div>
          );
        })}
      </nav>

      <SidebarFooter onResources={() => dispatch(goTo("resources"))} isCollapsed={isCollapsed} />
    </aside>
  );
}
